/**
 * Translates and merges duplicate filter values in the collection/search facets.
 *
 * On localized stores, Shopify returns the untranslated and the translated
 * metafield value as two separate filter values (e.g. "High Rise" and
 * "Taille haute"). On the French storefront, every filter title and value is
 * mapped 1:1 to its French label from FR_FACETS (looked up within its own
 * filter group), values that end up with the same label are collapsed into one,
 * and each filter's values are put in FR_FACETS order. Admin-defined aliases
 * (window.YJFacetAliases, one "Variant | Variant = Canonical" per line) catch
 * messy variants the master list doesn't cover. Matching ignores case, accents
 * and punctuation, so "ALEX ÉVASÉE", "Alex Évasée" and "Alex - Évasée" are
 * already the same value.
 *
 * Every merged value keeps its own <input>, so all underlying values are still
 * submitted and Shopify ORs them together.
 */
(function () {
  // English -> French master list, grouped by filter, in display order.
  const FR_FACETS = [
    {
      title: ['FITS', 'Silhouettes', 'COUPES'],
      values: [
        ['Alex - Bootcut', 'Alex - Évasée'],
        ['Chloe - Straight', 'Chloé - Droite'],
        ['Emily - Slim', 'Emily - Ajustée'],
        ['Lily - Wide', 'Lily - Très évasée'],
        ['Malia - Relaxed', 'Malia - Décontractée'],
        ['Rachel - Skinny', 'Rachel - Étroite'],
        ['Gabby - Loose Straight', 'Gabby - Loose Straight'],
        ['Dresses', 'Robes'],
        ['Jackets', 'Veste'],
        ['Jumpsuits', 'Combinaisons'],
        ['Linen', 'Lin'],
        ['Shirts', 'Chemise'],
        ['Shorts', 'Shorts'],
        ['Skirts', 'Jupes'],
      ],
    },
    {
      title: ['CATEGORY', 'Catégorie'],
      values: [
        ['Jeans', 'Jeans'],
        ['Capri', 'Capri'],
        ['Jackets', 'Veste'],
        ['Tops', 'Hauts'],
        ['Dresses', 'Robes'],
        ['Pants', 'Pantalons'],
        ['Shorts', 'Shorts'],
        ['Skirts', 'Jupes'],
        ['Jumpsuits', 'Combinaisons'],
        ['Linen', 'Lin'],
        ['Shirts', 'Chemise'],
      ],
    },
    {
      title: ['RISE', 'Taille'],
      values: [
        ['Mid Rise', 'Moyenne'],
        ['Classic Rise', 'Classique'],
        ['High Rise', 'Haute'],
        ['Sky Rise', 'Très Haute'],
        ['Linen', 'Lin'],
      ],
    },
    {
      title: ['INSEAMS', 'Entrejambe', 'ENTREJAMBES'],
      values: [
        ['Short (3"-10")', 'Short (3"-10")'],
        ['Capri (15"-18")', 'Capri (15"-18")'],
        ['Cropped (15"-25")', 'Court (15"-25")'],
        ['Ankled (26"-28")', 'Cheville (26"-28")'],
        ['Regular (29"-30")', 'Régulier (29"-30")'],
        ['Long (31"-33")', 'Long (31"-33")'],
        ['Extra Long (34"-36")', 'Très Long (34"-36")'],
      ],
    },
    {
      title: ['WASH', 'Lavage'],
      values: [
        ['White', 'Blanc'],
        ['Animal Print', 'Imprimé animal'],
        ['Light', 'Clair'],
        ['Medium', 'Moyen'],
        ['Dark', 'Foncé'],
        ['Colors', 'Couleurs'],
        ['Black', 'Noir'],
        ['Green', 'Vert'],
        ['Beige', 'Beige'],
        ['Brown', 'Brun'],
        ['Grey', 'Gris'],
        ['Yellow', 'Jaune'],
      ],
    },
    {
      title: ['FABRIC', 'Tissu'],
      values: [
        ['100% Cotton', '100 % coton'],
        ['Flow', 'Flow'],
        ['Travel', 'Voyage'],
        ['Vintage', 'Vintage'],
        ['100% Linen', '100 % lin'],
        ['Vegan Leather', 'Cuir végétalien'],
        ['Zen', 'Zen'],
      ],
    },
    {
      // Scoped: single letters like "S" or "L" are only translated inside this filter.
      title: ['SIZE', 'Taille'],
      scoped: true,
      values: [
        ['XXS', 'TTP'],
        ['XS', 'TP'],
        ['S', 'P'],
        ['M', 'M'],
        ['L', 'G'],
        ['XL', 'TG'],
        ['XXL', 'TTG'],
      ],
    },
    {
      title: ['PRICE', 'Prix'],
      values: [],
    },
  ];

  function normalize(str) {
    return String(str == null ? '' : str).toLowerCase().trim().replace(/\s+/g, ' ');
  }

  function foldAccents(str) {
    return str.normalize('NFD').replace(/[̀-ͯ]/g, '');
  }

  // Case, accent and punctuation-insensitive key ("Alex - Évasée" -> "alex evasee", "100 % lin" -> "100% lin").
  function looseKey(str) {
    return foldAccents(normalize(str))
      .replace(/[^a-z0-9%]+/g, ' ')
      .replace(/ %/g, '%')
      .trim();
  }

  function indexValues(values, index) {
    values.forEach(([en, fr], order) => {
      [en, fr].forEach((label) => {
        const key = looseKey(label);
        if (!(key in index)) index[key] = { label: fr, order };
      });
    });
    return index;
  }

  const GROUPS = FR_FACETS.map((group) => ({
    titleKeys: group.title.map(looseKey),
    title: group.title[1],
    scoped: Boolean(group.scoped),
    index: indexValues(group.values, {}),
  }));
  // Used when a filter's title isn't recognised.
  const UNGROUPED = FR_FACETS.filter((group) => !group.scoped).reduce((index, group) => indexValues(group.values, index), {});
  const ANY_GROUP = FR_FACETS.reduce((index, group) => indexValues(group.values, index), {});

  function facetGroups(title) {
    const key = looseKey(title);
    return key ? GROUPS.filter((group) => group.titleKeys.includes(key)) : [];
  }

  function parseAliases(text) {
    const aliases = {};
    String(text || '')
      .split(/\r?\n/)
      .forEach((line) => {
        const index = line.indexOf('=');
        if (index === -1) return;
        const canonical = line.slice(index + 1).trim();
        if (!canonical) return;
        line
          .slice(0, index)
          .split('|')
          .forEach((variant) => {
            const key = looseKey(variant);
            if (key) aliases[key] = canonical;
          });
      });
    return aliases;
  }

  // options: { aliases, locale, filter } — aliases and locale fall back to the window globals.
  function resolveOptions(options) {
    const opts = options || {};
    const rawAliases = opts.aliases !== undefined ? opts.aliases : typeof window !== 'undefined' ? window.YJFacetAliases : '';
    const aliases = typeof rawAliases === 'string' ? parseAliases(rawAliases) : rawAliases || {};
    const locale = opts.locale !== undefined ? opts.locale : typeof window !== 'undefined' ? window.YJFacetLocale : '';
    const french = /^fr\b/i.test(String(locale || ''));
    return { aliases, french, groups: french ? facetGroups(opts.filter) : [] };
  }

  function withFilter(ctx, filter) {
    return { aliases: ctx.aliases, french: ctx.french, groups: ctx.french ? facetGroups(filter) : [] };
  }

  function lookupMaster(label, ctx) {
    if (!ctx.french) return null;
    const key = looseKey(label);
    if (!ctx.groups.length) return UNGROUPED[key] || null;
    const group = ctx.groups.find((g) => g.index[key]);
    return group ? group.index[key] : null;
  }

  // The text a shopper ends up seeing for a raw value label, whether it was remapped,
  // and its position in the master list. Master list first, then aliases.
  function resolveLabel(label, ctx) {
    const own = lookupMaster(label, ctx);
    if (own) return { text: own.label, mapped: true, order: own.order };
    let alias = ctx.aliases[looseKey(label)];
    if (alias) {
      const target = lookupMaster(alias, ctx);
      if (target) return { text: target.label, mapped: true, order: target.order };
      // An alias pointing at another filter's value (e.g. "Haut" -> "Taille haute" in CATEGORY) doesn't apply here.
      if (ctx.groups.length && ANY_GROUP[looseKey(alias)]) alias = null;
    }
    return alias ? { text: alias, mapped: true, order: Infinity } : { text: label, mapped: false, order: Infinity };
  }

  function displayLabel(label, options) {
    return resolveLabel(label, resolveOptions(options)).text;
  }

  function canonicalKey(label, options) {
    return looseKey(displayLabel(label, options));
  }

  function isAllCaps(str) {
    return str === str.toUpperCase() && str !== str.toLowerCase();
  }

  // Mapped label first, then a non-ALL-CAPS label, so "ALEX ÉVASÉE" never wins.
  function pickLabel(labels, ctx) {
    const resolved = labels.map((label) => resolveLabel(label, ctx));
    return (resolved.find((r) => r.mapped) || resolved.find((r) => !isAllCaps(r.text)) || resolved[0]).text;
  }

  function countOf(item) {
    const match = (item.querySelector('.facets-checkbox__count') || {}).textContent;
    const digits = match ? match.match(/\d+/) : null;
    return digits ? parseInt(digits[0], 10) : 0;
  }

  function mergeGroup(items, ctx) {
    const inputs = items.map((item) => item.querySelector('input[data-value-label]'));
    const primaryIndex = Math.max(
      0,
      items.findIndex((item, i) => normalize(inputs[i].dataset.valueLabel) === normalize(resolveLabel(inputs[i].dataset.valueLabel, ctx).text))
    );
    const primary = items[primaryIndex];
    const primaryInput = inputs[primaryIndex];
    const primaryLabel = primary.querySelector('label');

    const total = items.reduce((sum, item) => sum + countOf(item), 0);
    const anyChecked = inputs.some((input) => input.checked);
    const allDisabled = inputs.every((input) => input.disabled);
    const aliasInputs = [];

    items.forEach((item, i) => {
      if (i === primaryIndex) return;
      const input = inputs[i];
      input.hidden = true;
      input.setAttribute('data-facet-alias', primaryInput.value);
      primaryLabel.appendChild(input);
      aliasInputs.push(input);
      item.remove();
    });

    const count = primary.querySelector('.facets-checkbox__count');
    if (count) count.textContent = `(${total})`;

    const text = primary.querySelector('.facets-checkbox__label');
    if (text) text.textContent = pickLabel(inputs.map((input) => input.dataset.valueLabel), ctx);

    if (anyChecked) {
      primaryInput.checked = true;
      aliasInputs.forEach((input) => {
        input.checked = true;
      });
    }

    primaryInput.disabled = allDisabled;
    primaryLabel.classList.toggle('facets-checkbox--disabled', allDisabled && !anyChecked);

    primaryInput.addEventListener('change', () => {
      aliasInputs.forEach((input) => {
        input.checked = primaryInput.checked;
      });
    });
  }

  function translateTitle(list, details, ctx) {
    if (!ctx.groups.length) return;
    const title = ctx.groups[0].title;
    const heading = details && details.querySelector('.facets__details-title');
    if (heading) heading.textContent = title;
    const legend = list && list.closest('fieldset') && list.closest('fieldset').querySelector('legend');
    if (legend) legend.textContent = title;
  }

  // Puts values in master-list order; values not in the list keep their relative order and go last.
  function sortItems(list, ctx) {
    const items = Array.from(list.children).filter((item) => item.matches('li.facets__item') && item.querySelector('input[data-value-label]:not([data-facet-alias])'));
    if (items.length < 2) return;
    const orderOf = new Map(
      items.map((item) => [item, resolveLabel(item.querySelector('input[data-value-label]:not([data-facet-alias])').dataset.valueLabel, ctx).order])
    );
    const sorted = items.slice().sort((a, b) => {
      const diff = orderOf.get(a) - orderOf.get(b);
      return Number.isNaN(diff) ? 0 : diff;
    });
    if (sorted.every((item, i) => item === items[i])) return;
    const anchor = document.createComment('');
    list.insertBefore(anchor, items[0]);
    sorted.forEach((item) => list.insertBefore(item, anchor));
    anchor.remove();
  }

  function dedupeFacetLists(root, options) {
    const scope = root || document;
    const base = resolveOptions(options);
    const debug = options && options.debug !== undefined ? Boolean(options.debug) : typeof location !== 'undefined' && /[?&]facet_debug\b/.test(location.search);
    // Filters without a value list (e.g. Price) only need their title translated.
    scope.querySelectorAll('details[data-filter-label]').forEach((details) => {
      if (!details.querySelector('ul.facets__list')) translateTitle(null, details, withFilter(base, details.dataset.filterLabel));
    });
    scope.querySelectorAll('ul.facets__list').forEach((list) => {
      const details = list.closest('details');
      const heading = details && details.querySelector('.facets__details-title');
      const filter = options && options.filter !== undefined ? options.filter : (details && details.dataset.filterLabel) || (heading && heading.textContent) || '';
      const ctx = withFilter(base, filter);
      translateTitle(list, details, ctx);

      const groups = new Map();
      Array.from(list.children).forEach((item) => {
        const input = item.matches('li.facets__item') && item.querySelector('input[data-value-label]');
        if (!input) return;
        const key = looseKey(resolveLabel(input.dataset.valueLabel, ctx).text);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(item);
      });
      groups.forEach((items) => {
        const parts = debug ? items.map((item) => `${item.querySelector('input[data-value-label]').dataset.valueLabel} ${countOf(item)}`) : [];
        if (items.length > 1) {
          mergeGroup(items, ctx);
        } else {
          // A lone value can still need its mapped spelling (e.g. "High Rise" -> "Taille haute").
          const input = items[0].querySelector('input[data-value-label]');
          const resolved = resolveLabel(input.dataset.valueLabel, ctx);
          const text = items[0].querySelector('.facets-checkbox__label');
          if (resolved.mapped && text) text.textContent = resolved.text;
        }
        // ?facet_debug lists the raw values behind each label and their counts.
        const text = debug && items.find((item) => item.isConnected).querySelector('.facets-checkbox__label');
        if (text) text.textContent += ` [${parts.join(' + ')}]`;
      });

      if (ctx.french) sortItems(list, ctx);
    });
  }

  // { [param]: { title, values: [{ value, label }] } } for every filter checkbox under root.
  function readFilters(root) {
    const filters = {};
    root.querySelectorAll('input[type="checkbox"][name^="filter."]').forEach((input) => {
      const details = input.closest('[data-filter-label]');
      const filter = filters[input.name] || (filters[input.name] = { title: '', values: [] });
      if (!filter.title && details) filter.title = details.dataset.filterLabel;
      if (!filter.values.some((v) => v.value === input.value)) filter.values.push({ value: input.value, label: input.dataset.valueLabel || input.value });
    });
    return filters;
  }

  // Rewrites the filter.* params of a query string for another language's filter values:
  // a value the target has is kept, otherwise it becomes the target value(s) with the same
  // master-list meaning ("Haute" -> "High Rise"), and anything unmatched is dropped.
  // options: { sourceFilters, aliases } — sourceFilters gives the labels behind the current values.
  function mapFilterParams(search, targetFilters, options) {
    const opts = options || {};
    const sourceFilters = opts.sourceFilters || {};
    const base = resolveOptions({ aliases: opts.aliases, locale: 'fr' });
    const meaning = (label, ctx) => looseKey(resolveLabel(label, ctx).text);
    const out = new URLSearchParams();
    const seen = new Set();
    const add = (name, value) => {
      const pair = `${name}=${value}`;
      if (seen.has(pair)) return;
      seen.add(pair);
      out.append(name, value);
    };

    new URLSearchParams(search).forEach((value, name) => {
      const target = targetFilters[name];
      // Non-filter params (sort_by, q, …) and price ranges carry over as-is.
      if (!name.startsWith('filter.') || /\.(gte|lte)$/.test(name)) return add(name, value);
      if (!target) return;
      if (target.values.some((v) => v.value === value)) return add(name, value);
      const source = sourceFilters[name] || {};
      const own = (source.values || []).find((v) => v.value === value);
      const ctx = withFilter(base, target.title || source.title || '');
      const key = meaning(own ? own.label : value, ctx);
      target.values.filter((v) => meaning(v.label, ctx) === key || meaning(v.value, ctx) === key).forEach((v) => add(name, v.value));
    });
    return out.toString();
  }

  // URL of the current page (without query) in another language, from Shopify's hreflang links.
  function localePath(localeCode) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const path = root !== '/' && location.pathname.startsWith(root) ? `/${location.pathname.slice(root.length)}` : location.pathname;
    const code = String(localeCode).toLowerCase();
    const link = Array.from(document.querySelectorAll('link[rel="alternate"][hreflang]')).find((el) => {
      const lang = el.getAttribute('hreflang').toLowerCase();
      return lang === code || lang.startsWith(`${code}-`);
    });
    return { path, target: link ? new URL(link.href, location.href).pathname : `/${code}${path === '/' ? '' : path}` };
  }

  // Before the language switcher submits, maps the active filters to the target language's
  // values so the shopper doesn't land on an empty page. Falls back to dropping the filters.
  async function prepareLocaleSwitch(form, localeCode, options) {
    const opts = options || {};
    const params = new URLSearchParams(location.search);
    const grid = document.getElementById('product-grid');
    if (!form || !grid || !Array.from(params.keys()).some((name) => name.startsWith('filter.'))) return;
    let returnTo = form.querySelector('input[name="return_to"]');
    if (!returnTo) {
      returnTo = document.createElement('input');
      returnTo.type = 'hidden';
      returnTo.name = 'return_to';
      form.appendChild(returnTo);
    }
    const { path, target } = localePath(localeCode);
    let query;
    try {
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const timer = setTimeout(() => controller && controller.abort(), opts.timeout || 1500);
      const response = await fetch(`${target}?section_id=${encodeURIComponent(grid.dataset.id)}`, controller ? { signal: controller.signal } : undefined);
      const html = await response.text();
      clearTimeout(timer);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      query = mapFilterParams(location.search, readFilters(doc), { sourceFilters: readFilters(document) });
    } catch (error) {
      Array.from(params.keys())
        .filter((name) => name.startsWith('filter.'))
        .forEach((name) => params.delete(name));
      query = params.toString();
    }
    returnTo.value = query ? `${path}?${query}` : path;
  }

  const api = { FR_FACETS, normalize, looseKey, parseAliases, displayLabel, canonicalKey, dedupeFacetLists, readFilters, mapFilterParams, prepareLocaleSwitch };

  if (typeof window !== 'undefined') {
    window.YJFacetDedupe = api;
    if (typeof document !== 'undefined') {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => dedupeFacetLists(document));
      } else {
        dedupeFacetLists(document);
      }
    }
  }

  // primarily for unit testing purposes
  if (typeof module !== 'undefined') {
    module.exports = api;
  }
})();
